import type { TFunction } from "i18next";

/** The rejection of the API client for a PIN route: `{ code, message, data }`. */
type PinError = { code?: string; data?: { remainingAttempts?: unknown } };

/** Text of a failed PIN call, shown by the lock screen (MED-1621). */
export const pinErrorMessage = (t: TFunction, error: unknown): string => {
  const { code, data } = (error ?? {}) as PinError;
  switch (code) {
    case "PIN_INVALID": {
      const remaining = data?.remainingAttempts;
      return typeof remaining === "number"
        ? t("pinLock.errors.invalidWithAttempts", { count: remaining })
        : t("pinLock.errors.invalid");
    }
    case "PIN_INVALID_FORMAT":
      return t("pinLock.errors.invalidFormat");
    case "PIN_ALREADY_SET":
      return t("pinLock.errors.alreadySet");
    case "PIN_ATTEMPTS_EXCEEDED":
      return t("pinLock.errors.attemptsExceeded");
    case "PIN_RESET_OTP_INVALID":
      return t("pinLock.errors.otpInvalid");
    case "PIN_RESET_RATE_LIMITED":
      return t("pinLock.errors.rateLimited");
    case "PIN_CREATE_REAUTH_REQUIRED":
      return t("pinLock.forcedLogout.REAUTH_REQUIRED");
    case "SESSION_REUSE_DETECTED":
      return t("pinLock.forcedLogout.SESSION_REUSE");
    default:
      return t("pinLock.errors.generic");
  }
};

/** Key of the locked flag kept by useIdleLock in localStorage (shared between tabs). */
export const PIN_LOCK_STORAGE_KEY = "medecine360-dashboard:pin-lock";

/** MED-1643: delay before the screen locks, chosen by the user in the settings. */
export const DEFAULT_PIN_LOCK_MINUTES = 15;
export const PIN_LOCK_MINUTES_OPTIONS = [5, 15, 30, 60] as const;

/** Inactivity delay of the lock screen; the session's `pinLockMinutes`, 15 minutes when absent. */
export const pinLockTimeoutMs = (pinLockMinutes?: number | null): number =>
  (pinLockMinutes ?? DEFAULT_PIN_LOCK_MINUTES) * 60_000;

/**
 * Forgets the locked flag of the lock screen: a fresh login (sessionToken in the URL) or a sign
 * out must not open the next session locked. useIdleLock reads the flag when it mounts.
 */
export const clearPersistedPinLock = (): void => {
  try {
    window.localStorage.removeItem(PIN_LOCK_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode): nothing was kept.
  }
};
